import { useState, useEffect } from "react";
import { Clock, Mail, Radio, Wrench, Lock, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaintenancePage = ({ message, estimatedReturn, contactEmail, onAdminLogin }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    if (!estimatedReturn) return;
    const target = new Date(estimatedReturn).getTime();
    if (isNaN(target)) return;

    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setTimeLeft(null); return; }
      setTimeLeft({
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [estimatedReturn]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.detail || "Login failed"); return; }
      if (!data.user?.is_admin) { setLoginError("Admin access only during maintenance."); return; }
      localStorage.setItem("token", data.token);
      if (onAdminLogin) onAdminLogin(data.user, data.token);
    } catch {
      setLoginError("Connection failed. Try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6" data-testid="maintenance-page">
      <div className="max-w-lg w-full text-center">
        {/* Logo */}
        <div className="mb-10">
          <img
            src="/logo-header.png"
            alt="DJsandMCMedia"
            className="h-12 mx-auto mb-6 opacity-80"
            data-testid="maintenance-logo"
          />
        </div>

        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
          <Wrench className="w-9 h-9 text-purple-400 animate-pulse" />
        </div>

        {/* Message */}
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-4" data-testid="maintenance-heading">
          We'll Be Right Back
        </h1>
        <p className="text-white/50 text-base leading-relaxed mb-10" data-testid="maintenance-message">
          {message || "We're upgrading the platform. We'll be back shortly."}
        </p>

        {/* Countdown */}
        {timeLeft && (
          <div className="flex justify-center gap-4 mb-10" data-testid="maintenance-countdown">
            {[
              { value: timeLeft.hours, label: "Hours" },
              { value: timeLeft.minutes, label: "Minutes" },
              { value: timeLeft.seconds, label: "Seconds" },
            ].map((unit) => (
              <div key={unit.label} className="bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-4 min-w-[80px]">
                <p className="text-3xl font-black text-white tabular-nums">{pad(unit.value)}</p>
                <p className="text-white/30 text-xs mt-1 uppercase tracking-wider">{unit.label}</p>
              </div>
            ))}
          </div>
        )}

        {!timeLeft && estimatedReturn && (
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm mb-10" data-testid="maintenance-eta">
            <Clock className="w-4 h-4" />
            <span>Estimated return: {new Date(estimatedReturn).toLocaleString()}</span>
          </div>
        )}

        {/* Contact */}
        {contactEmail && (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 inline-flex items-center gap-3" data-testid="maintenance-contact">
            <Mail className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <p className="text-white/50 text-sm">
              Questions? Reach us at{" "}
              <a href={`mailto:${contactEmail}`} className="text-purple-400 hover:text-purple-300 transition underline">
                {contactEmail}
              </a>
            </p>
          </div>
        )}

        {/* Admin Login */}
        <div className="mt-10">
          {!showLogin ? (
            <button
              onClick={() => setShowLogin(true)}
              className="text-white/15 hover:text-white/40 text-xs transition inline-flex items-center gap-1"
              data-testid="maintenance-admin-link"
            >
              <Lock className="w-3 h-3" /> Admin Access
            </button>
          ) : (
            <form onSubmit={handleAdminLogin} className="max-w-xs mx-auto bg-white/[0.03] border border-white/10 rounded-2xl p-5 text-left" data-testid="maintenance-admin-form">
              <p className="text-white/60 text-xs font-medium mb-3 text-center">Admin Login</p>
              {loginError && <p className="text-red-400 text-xs mb-3 text-center" data-testid="maintenance-login-error">{loginError}</p>}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin email"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-2 focus:outline-none focus:border-purple-500"
                data-testid="maintenance-login-email"
              />
              <div className="relative mb-3">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm pr-9 focus:outline-none focus:border-purple-500"
                  data-testid="maintenance-login-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-2 text-white/30 hover:text-white/60">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white text-sm font-medium py-2 rounded-lg transition"
                data-testid="maintenance-login-btn"
              >
                {loggingIn ? "Signing in..." : "Sign In"}
              </button>
              <button type="button" onClick={() => setShowLogin(false)} className="w-full text-white/30 text-xs mt-2 hover:text-white/50 transition">Cancel</button>
            </form>
          )}
        </div>

        {/* Licensed line */}
        <p className="text-white/20 text-xs mt-12 flex items-center justify-center gap-1.5">
          <Radio className="w-3 h-3" />
          DJsandMCMedia operates as a licensed internet radio platform.
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
